/**
 * 게이미피케이션 시스템
 * 포인트, 배지, 레벨, 업적, 보상 관리
 */

// 포인트 획득 액션 정의
export const POINT_ACTIONS = {
  // 기본 학습 활동
  CORRECT_ANSWER: { points: 10, message: '정답! 🎉' },
  FIRST_TRY_CORRECT: { points: 15, message: '첫 시도 정답! ⭐' },
  STREAK_5: { points: 25, message: '5연속 정답! 🔥' },
  STREAK_10: { points: 50, message: '10연속 정답! 🚀' },
  STREAK_15: { points: 100, message: '15연속 정답! 💎' },
  
  // 시험 완료
  EXAM_COMPLETE: { points: 50, message: '시험 완료! 📝' },
  PERFECT_SCORE: { points: 200, message: '만점! 👑' },
  HIGH_SCORE_90: { points: 100, message: '90점 이상! 🌟' },
  HIGH_SCORE_80: { points: 50, message: '80점 이상! ✨' },
  
  // 학습 패턴
  DAILY_LOGIN: { points: 5, message: '출석 체크! 📅' },
  WEEKLY_GOAL: { points: 100, message: '주간 목표 달성! 🎯' },
  STUDY_STREAK_7: { points: 150, message: '7일 연속 학습! 📚' },
  STUDY_STREAK_30: { points: 500, message: '30일 연속 학습! 🏆' },
  
  // 피드백 및 참여
  FEEDBACK_SUBMIT: { points: 20, message: '피드백 감사! 💬' },
  BETA_PARTICIPATION: { points: 50, message: '베타 참여 감사! 🧪' },
  FEATURE_DISCOVERY: { points: 30, message: '새 기능 발견! ✨' },
  
  // 시간 기반
  LONG_SESSION_30MIN: { points: 25, message: '30분 집중 학습! ⏰' },
  LONG_SESSION_60MIN: { points: 50, message: '1시간 집중 학습! 💪' },
  LONG_SESSION_120MIN: { points: 100, message: '2시간 집중 학습! 🔋' },
  
  // 특별 활동
  FIRST_EXAM: { points: 30, message: '첫 시험 도전! 🚀' },
  BOOKMARK_USE: { points: 5, message: '북마크 활용! 📌' },
  RETRY_ATTEMPT: { points: 5, message: '재도전 정신! 💪' }
};

// 배지 정의
export const BADGES = {
  // 학습 기본 배지
  BEGINNER: {
    id: 'beginner',
    name: '학습 시작',
    description: '첫 번째 문제를 맞혔습니다',
    icon: '🌱',
    rarity: 'common',
    requirement: { type: 'correct_answers', value: 1 }
  },
  SCHOLAR: {
    id: 'scholar',
    name: '학자',
    description: '100개 문제를 맞혔습니다',
    icon: '📚',
    rarity: 'rare',
    requirement: { type: 'correct_answers', value: 100 }
  },
  EXPERT: {
    id: 'expert',
    name: '전문가',
    description: '500개 문제를 맞혔습니다',
    icon: '🎓',
    rarity: 'epic',
    requirement: { type: 'correct_answers', value: 500 }
  },
  MASTER: {
    id: 'master',
    name: '마스터',
    description: '1000개 문제를 맞혔습니다',
    icon: '👑',
    rarity: 'legendary',
    requirement: { type: 'correct_answers', value: 1000 }
  },
  
  // 연속 정답 배지
  STREAK_ROOKIE: {
    id: 'streak_rookie',
    name: '연승 신입',
    description: '5연속 정답을 달성했습니다',
    icon: '🔥',
    rarity: 'common',
    requirement: { type: 'max_streak', value: 5 }
  },
  STREAK_CHAMPION: {
    id: 'streak_champion',
    name: '연승 챔피언',
    description: '15연속 정답을 달성했습니다',
    icon: '⚡',
    rarity: 'epic',
    requirement: { type: 'max_streak', value: 15 }
  },
  STREAK_LEGEND: {
    id: 'streak_legend',
    name: '연승 레전드',
    description: '30연속 정답을 달성했습니다',
    icon: '💎',
    rarity: 'legendary',
    requirement: { type: 'max_streak', value: 30 }
  },
  
  // 시험 완료 배지
  EXAM_WARRIOR: {
    id: 'exam_warrior',
    name: '시험 전사',
    description: '10개 시험을 완주했습니다',
    icon: '⚔️',
    rarity: 'rare',
    requirement: { type: 'exams_completed', value: 10 }
  },
  EXAM_CONQUEROR: {
    id: 'exam_conqueror',
    name: '시험 정복자',
    description: '50개 시험을 완주했습니다',
    icon: '🏰',
    rarity: 'epic',
    requirement: { type: 'exams_completed', value: 50 }
  },
  
  // 점수 관련 배지
  PERFECTIONIST: {
    id: 'perfectionist',
    name: '완벽주의자',
    description: '만점을 5번 달성했습니다',
    icon: '💯',
    rarity: 'legendary',
    requirement: { type: 'perfect_scores', value: 5 }
  },
  HIGH_ACHIEVER: {
    id: 'high_achiever',
    name: '고득점자',
    description: '90점 이상을 20번 달성했습니다',
    icon: '🌟',
    rarity: 'epic',
    requirement: { type: 'high_scores_90', value: 20 }
  },
  
  // 참여 배지
  BETA_PIONEER: {
    id: 'beta_pioneer',
    name: '베타 개척자',
    description: '베타 테스터로 참여했습니다',
    icon: '🧪',
    rarity: 'rare',
    requirement: { type: 'beta_user', value: true }
  },
  FEEDBACK_HERO: {
    id: 'feedback_hero',
    name: '피드백 영웅',
    description: '50개 피드백을 제출했습니다',
    icon: '💬',
    rarity: 'epic',
    requirement: { type: 'feedback_count', value: 50 }
  },
  
  // 시간/노력 배지
  MARATHON_RUNNER: {
    id: 'marathon_runner',
    name: '마라톤 러너',
    description: '2시간 연속 학습했습니다',
    icon: '🏃',
    rarity: 'rare',
    requirement: { type: 'longest_session', value: 7200 } // 2시간(초)
  },
  NIGHT_OWL: {
    id: 'night_owl',
    name: '올빼미',
    description: '자정 이후에 학습했습니다',
    icon: '🦉',
    rarity: 'uncommon',
    requirement: { type: 'late_night_study', value: true }
  },
  EARLY_BIRD: {
    id: 'early_bird',
    name: '일찍 일어나는 새',
    description: '오전 6시 이전에 학습했습니다',
    icon: '🐦',
    rarity: 'uncommon',
    requirement: { type: 'early_morning_study', value: true }
  },
  
  // 특별 배지
  EXPLORER: {
    id: 'explorer',
    name: '탐험가',
    description: '모든 주요 기능을 사용했습니다',
    icon: '🗺️',
    rarity: 'epic',
    requirement: { type: 'features_used', value: 5 }
  }
};

// 레벨 시스템
export const LEVELS = [
  { level: 1, minPoints: 0, title: '새싹', color: '#4ade80', icon: '🌱' },
  { level: 2, minPoints: 100, title: '학습자', color: '#22c55e', icon: '📖' },
  { level: 3, minPoints: 300, title: '노력가', color: '#16a34a', icon: '💪' },
  { level: 4, minPoints: 600, title: '도전자', color: '#3b82f6', icon: '🎯' },
  { level: 5, minPoints: 1000, title: '연구자', color: '#1d4ed8', icon: '🔬' },
  { level: 6, minPoints: 1500, title: '전문가', color: '#7c3aed', icon: '🎓' },
  { level: 7, minPoints: 2200, title: '숙련자', color: '#c026d3', icon: '⭐' },
  { level: 8, minPoints: 3000, title: '마스터', color: '#db2777', icon: '👑' },
  { level: 9, minPoints: 4000, title: '레전드', color: '#dc2626', icon: '🏆' },
  { level: 10, minPoints: 5500, title: '신화', color: '#ea580c', icon: '💎' }
];

// 일일/주간 미션
export const MISSIONS = {
  DAILY: [
    {
      id: 'daily_questions_5',
      name: '5문제 도전',
      description: '오늘 5문제를 맞혀보세요',
      target: 5,
      reward: { points: 30, type: 'points' },
      type: 'correct_answers_today'
    },
    {
      id: 'daily_login',
      name: '출석 체크',
      description: '오늘 서비스에 접속하세요',
      target: 1,
      reward: { points: 10, type: 'points' },
      type: 'daily_login'
    },
    {
      id: 'daily_feedback',
      name: '피드백 제출',
      description: '오늘 피드백을 1개 제출하세요',
      target: 1,
      reward: { points: 25, type: 'points' },
      type: 'feedback_today'
    }
  ],
  WEEKLY: [
    {
      id: 'weekly_exam_complete',
      name: '주간 시험 완주',
      description: '이번 주에 3개 시험을 완주하세요',
      target: 3,
      reward: { points: 100, type: 'points' },
      type: 'exams_this_week'
    },
    {
      id: 'weekly_streak',
      name: '주간 연승',
      description: '이번 주에 10연승을 달성하세요',
      target: 10,
      reward: { points: 80, badge: 'STREAK_MASTER' },
      type: 'max_streak_this_week'
    }
  ]
};

// 게이미피케이션 시스템 클래스
export class GamificationSystem {
  constructor(userId = 'guest') {
    this.userId = userId;
    this.storageKey = `gamification_${userId}`;
    this.data = this.loadData();
  }

  // 데이터 로드
  loadData() {
    try {
      const saved = localStorage.getItem(this.storageKey);
      if (saved) {
        const data = JSON.parse(saved);
        // featuresUsed 배열을 Set으로 변환
        if (Array.isArray(data.featuresUsed)) {
          data.featuresUsed = new Set(data.featuresUsed);
        } else if (!data.featuresUsed || !(data.featuresUsed instanceof Set)) {
          data.featuresUsed = new Set();
        }
        return data;
      }
    } catch (error) {
      console.error('Failed to load gamification data:', error);
    }

    // 기본 데이터
    return {
      points: 0,
      level: 1,
      totalCorrectAnswers: 0,
      totalExamsCompleted: 0,
      maxStreak: 0,
      currentStreak: 0,
      perfectScores: 0,
      highScores90: 0,
      feedbackCount: 0,
      longestSession: 0,
      featuresUsed: new Set(),
      badges: [],
      dailyStats: {},
      weeklyStats: {},
      lastLogin: null,
      studyStreak: 0,
      totalStudyTime: 0,
      createdAt: new Date().toISOString()
    };
  }

  // 데이터 저장
  saveData() {
    try {
      // Set을 Array로 변환하여 저장
      const dataToSave = {
        ...this.data,
        featuresUsed: Array.from(this.data.featuresUsed)
      };
      localStorage.setItem(this.storageKey, JSON.stringify(dataToSave));
    } catch (error) {
      console.error('Failed to save gamification data:', error);
    }
  }

  // 포인트 추가
  addPoints(actionKey, context = {}) {
    const action = POINT_ACTIONS[actionKey];
    if (!action) return null;

    const oldLevel = this.getCurrentLevel();
    this.data.points += action.points;
    const newLevel = this.getCurrentLevel();

    const result = {
      action: actionKey,
      points: action.points,
      totalPoints: this.data.points,
      message: action.message,
      levelUp: newLevel.level > oldLevel.level,
      newLevel: newLevel
    };

    // 레벨업 시 추가 보너스
    if (result.levelUp) {
      const bonusPoints = newLevel.level * 20;
      this.data.points += bonusPoints;
      result.levelUpBonus = bonusPoints;
      result.totalPoints = this.data.points;
    }

    this.saveData();

    // 이벤트 발생
    this.dispatchEvent('pointsEarned', result);

    return result;
  }

  // 현재 레벨 계산
  getCurrentLevel() {
    let currentLevel = LEVELS[0];
    for (const level of LEVELS) {
      if (this.data.points >= level.minPoints) {
        currentLevel = level;
      } else {
        break;
      }
    }
    return currentLevel;
  }

  // 다음 레벨까지 필요한 포인트
  getPointsToNextLevel() {
    const currentLevel = this.getCurrentLevel();
    const currentIndex = LEVELS.findIndex(l => l.level === currentLevel.level);
    
    if (currentIndex === LEVELS.length - 1) {
      return 0; // 최고 레벨
    }

    const nextLevel = LEVELS[currentIndex + 1];
    return nextLevel.minPoints - this.data.points;
  }

  // 진행률 계산
  getLevelProgress() {
    const currentLevel = this.getCurrentLevel();
    const currentIndex = LEVELS.findIndex(l => l.level === currentLevel.level);
    
    if (currentIndex === LEVELS.length - 1) {
      return 100; // 최고 레벨
    }

    const nextLevel = LEVELS[currentIndex + 1];
    const pointsInCurrentLevel = this.data.points - currentLevel.minPoints;
    const pointsNeededForLevel = nextLevel.minPoints - currentLevel.minPoints;
    
    return Math.round((pointsInCurrentLevel / pointsNeededForLevel) * 100);
  }

  // 학습 활동 기록
  recordCorrectAnswer(streak = 0) {
    this.data.totalCorrectAnswers++;
    this.data.currentStreak = streak;
    this.data.maxStreak = Math.max(this.data.maxStreak, streak);

    // 포인트 지급
    let pointsAwarded = [];
    pointsAwarded.push(this.addPoints('CORRECT_ANSWER'));

    // 연속 정답 보너스
    if (streak === 5) pointsAwarded.push(this.addPoints('STREAK_5'));
    if (streak === 10) pointsAwarded.push(this.addPoints('STREAK_10'));
    if (streak === 15) pointsAwarded.push(this.addPoints('STREAK_15'));

    // 배지 확인
    this.checkAndAwardBadges();

    return pointsAwarded.filter(p => p !== null);
  }

  // 시험 완료 기록
  recordExamCompletion(score) {
    this.data.totalExamsCompleted++;

    let pointsAwarded = [];
    pointsAwarded.push(this.addPoints('EXAM_COMPLETE'));

    if (score === 100) {
      this.data.perfectScores++;
      pointsAwarded.push(this.addPoints('PERFECT_SCORE'));
    } else if (score >= 90) {
      this.data.highScores90++;
      pointsAwarded.push(this.addPoints('HIGH_SCORE_90'));
    } else if (score >= 80) {
      pointsAwarded.push(this.addPoints('HIGH_SCORE_80'));
    }

    this.checkAndAwardBadges();
    return pointsAwarded.filter(p => p !== null);
  }

  // 피드백 제출 기록
  recordFeedback() {
    this.data.feedbackCount++;
    const pointsAwarded = this.addPoints('FEEDBACK_SUBMIT');
    this.checkAndAwardBadges();
    return pointsAwarded;
  }

  // 기능 사용 기록
  recordFeatureUsage(featureName) {
    // featuresUsed가 Set이 아닌 경우 Set으로 초기화
    if (!(this.data.featuresUsed instanceof Set)) {
      this.data.featuresUsed = new Set(Array.isArray(this.data.featuresUsed) ? this.data.featuresUsed : []);
    }
    
    if (!this.data.featuresUsed.has(featureName)) {
      this.data.featuresUsed.add(featureName);
      const pointsAwarded = this.addPoints('FEATURE_DISCOVERY');
      this.checkAndAwardBadges();
      return pointsAwarded;
    }
    return null;
  }

  // 세션 시간 기록
  recordSessionTime(seconds) {
    this.data.longestSession = Math.max(this.data.longestSession, seconds);
    this.data.totalStudyTime += seconds;

    let pointsAwarded = [];
    if (seconds >= 1800 && seconds < 3600) { // 30분-1시간
      pointsAwarded.push(this.addPoints('LONG_SESSION_30MIN'));
    } else if (seconds >= 3600 && seconds < 7200) { // 1-2시간
      pointsAwarded.push(this.addPoints('LONG_SESSION_60MIN'));
    } else if (seconds >= 7200) { // 2시간 이상
      pointsAwarded.push(this.addPoints('LONG_SESSION_120MIN'));
    }

    this.checkAndAwardBadges();
    return pointsAwarded.filter(p => p !== null);
  }

  // 배지 확인 및 지급
  checkAndAwardBadges() {
    const newBadges = [];

    Object.values(BADGES).forEach(badge => {
      if (this.data.badges.includes(badge.id)) return;

      let earned = false;
      const req = badge.requirement;

      switch (req.type) {
        case 'correct_answers':
          earned = this.data.totalCorrectAnswers >= req.value;
          break;
        case 'max_streak':
          earned = this.data.maxStreak >= req.value;
          break;
        case 'exams_completed':
          earned = this.data.totalExamsCompleted >= req.value;
          break;
        case 'perfect_scores':
          earned = this.data.perfectScores >= req.value;
          break;
        case 'high_scores_90':
          earned = this.data.highScores90 >= req.value;
          break;
        case 'feedback_count':
          earned = this.data.feedbackCount >= req.value;
          break;
        case 'longest_session':
          earned = this.data.longestSession >= req.value;
          break;
        case 'features_used':
          // featuresUsed가 Set이 아닌 경우 처리
          if (!(this.data.featuresUsed instanceof Set)) {
            this.data.featuresUsed = new Set(Array.isArray(this.data.featuresUsed) ? this.data.featuresUsed : []);
          }
          earned = this.data.featuresUsed.size >= req.value;
          break;
        case 'beta_user':
          // 외부에서 설정됨
          break;
      }

      if (earned) {
        this.data.badges.push(badge.id);
        newBadges.push(badge);
        
        // 배지 획득 보너스 포인트
        const bonusPoints = this.getBadgeBonus(badge.rarity);
        if (bonusPoints > 0) {
          this.data.points += bonusPoints;
        }
      }
    });

    if (newBadges.length > 0) {
      this.saveData();
      this.dispatchEvent('badgesEarned', newBadges);
    }

    return newBadges;
  }

  // 배지 희귀도별 보너스 포인트
  getBadgeBonus(rarity) {
    const bonuses = {
      common: 10,
      uncommon: 25,
      rare: 50,
      epic: 100,
      legendary: 200
    };
    return bonuses[rarity] || 0;
  }

  // 획득한 배지 목록
  getEarnedBadges() {
    return this.data.badges.map(id => 
      Object.values(BADGES).find(badge => badge.id === id)
    ).filter(Boolean);
  }

  // 사용자 통계
  getStats() {
    const level = this.getCurrentLevel();
    const progress = this.getLevelProgress();
    const pointsToNext = this.getPointsToNextLevel();

    return {
      points: this.data.points,
      level: level.level,
      levelTitle: level.title,
      levelColor: level.color,
      levelIcon: level.icon,
      progress: progress,
      pointsToNextLevel: pointsToNext,
      totalCorrectAnswers: this.data.totalCorrectAnswers,
      totalExamsCompleted: this.data.totalExamsCompleted,
      maxStreak: this.data.maxStreak,
      currentStreak: this.data.currentStreak,
      badgeCount: this.data.badges.length,
      totalStudyTime: this.data.totalStudyTime,
      averageSessionTime: this.data.totalExamsCompleted > 0 
        ? Math.round(this.data.totalStudyTime / this.data.totalExamsCompleted) 
        : 0
    };
  }

  // 이벤트 발생
  dispatchEvent(type, detail) {
    window.dispatchEvent(new CustomEvent(`gamification${type}`, { detail }));
  }

  // 베타 사용자 배지 수동 지급
  awardBetaBadge() {
    if (!this.data.badges.includes('beta_pioneer')) {
      this.data.badges.push('beta_pioneer');
      const badge = BADGES.BETA_PIONEER;
      const bonusPoints = this.getBadgeBonus(badge.rarity);
      this.data.points += bonusPoints;
      this.saveData();
      this.dispatchEvent('badgesEarned', [badge]);
      return badge;
    }
    return null;
  }
}

// 전역 인스턴스 관리
let globalGamificationInstance = null;

export function getGamificationSystem(userId = 'guest') {
  if (!globalGamificationInstance || globalGamificationInstance.userId !== userId) {
    globalGamificationInstance = new GamificationSystem(userId);
  }
  return globalGamificationInstance;
}

// 개발용: 오래된 gamification 데이터 초기화
export function resetGamificationData(userId = 'guest') {
  const storageKey = `gamification_${userId}`;
  localStorage.removeItem(storageKey);
  if (globalGamificationInstance && globalGamificationInstance.userId === userId) {
    globalGamificationInstance = null;
  }
  return getGamificationSystem(userId);
}

// 사용자 ID 변경 시 데이터 마이그레이션 (게스트 → 실제 로그인)
export function migrateGamificationData(oldUserId, newUserId) {
  if (!oldUserId || !newUserId || oldUserId === newUserId) {
    return false;
  }

  const oldStorageKey = `gamification_${oldUserId}`;
  const newStorageKey = `gamification_${newUserId}`;
  
  try {
    // 기존 데이터 확인
    const oldData = localStorage.getItem(oldStorageKey);
    if (!oldData) {
      return false;
    }

    // 새 사용자 데이터가 이미 있는지 확인
    const existingNewData = localStorage.getItem(newStorageKey);
    if (existingNewData) {
      // 기존 데이터가 있으면 병합 로직 필요
      const oldStats = JSON.parse(oldData);
      const newStats = JSON.parse(existingNewData);
      
      // 더 높은 값들로 병합
      const mergedData = {
        ...newStats,
        points: Math.max(oldStats.points, newStats.points),
        totalCorrectAnswers: Math.max(oldStats.totalCorrectAnswers, newStats.totalCorrectAnswers),
        totalExamsCompleted: Math.max(oldStats.totalExamsCompleted, newStats.totalExamsCompleted),
        maxStreak: Math.max(oldStats.maxStreak, newStats.maxStreak),
        perfectScores: Math.max(oldStats.perfectScores, newStats.perfectScores),
        highScores90: Math.max(oldStats.highScores90, newStats.highScores90),
        feedbackCount: Math.max(oldStats.feedbackCount, newStats.feedbackCount),
        longestSession: Math.max(oldStats.longestSession, newStats.longestSession),
        totalStudyTime: Math.max(oldStats.totalStudyTime, newStats.totalStudyTime),
        // 배지는 합집합
        badges: [...new Set([...oldStats.badges, ...newStats.badges])],
        // 기능 사용은 합집합
        featuresUsed: [...new Set([...(oldStats.featuresUsed || []), ...(newStats.featuresUsed || [])])]
      };
      
      localStorage.setItem(newStorageKey, JSON.stringify(mergedData));
    } else {
      // 새 데이터가 없으면 단순 이동
      localStorage.setItem(newStorageKey, oldData);
    }
    
    // 기존 데이터 삭제
    localStorage.removeItem(oldStorageKey);
    
    // 글로벌 인스턴스 갱신
    if (globalGamificationInstance && globalGamificationInstance.userId === oldUserId) {
      globalGamificationInstance = null;
    }
    
    console.log(`Gamification data migrated from ${oldUserId} to ${newUserId}`);
    return true;
    
  } catch (error) {
    console.error('Failed to migrate gamification data:', error);
    return false;
  }
}

// 편의 함수들 - 사용자 ID 기반
export const gamificationActions = {
  addPoints: (actionKey, context, userId = 'guest') => getGamificationSystem(userId).addPoints(actionKey, context),
  recordCorrectAnswer: (streak, userId = 'guest') => getGamificationSystem(userId).recordCorrectAnswer(streak),
  recordExamCompletion: (score, userId = 'guest') => getGamificationSystem(userId).recordExamCompletion(score),
  recordFeedback: (userId = 'guest') => getGamificationSystem(userId).recordFeedback(),
  recordFeatureUsage: (featureName, userId = 'guest') => getGamificationSystem(userId).recordFeatureUsage(featureName),
  recordSessionTime: (seconds, userId = 'guest') => getGamificationSystem(userId).recordSessionTime(seconds),
  getStats: (userId = 'guest') => getGamificationSystem(userId).getStats(),
  getEarnedBadges: (userId = 'guest') => getGamificationSystem(userId).getEarnedBadges(),
  awardBetaBadge: (userId = 'guest') => getGamificationSystem(userId).awardBetaBadge(),
  migrateData: (oldUserId, newUserId) => migrateGamificationData(oldUserId, newUserId)
};